import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import fetchEsimPromoCodesAction from 'actions/loyalty/fetchEsimPromoCodes'
import { buildEsimPromoCodesView, EMPTY_ESIM_PROMO_CODES_VIEW, type EsimPromoCodesView } from 'loyalty/lib/esims/esimPromoCodesView'

interface UseAccountEsimPromoCodesOptions {
  enabled?: boolean
}

/**
 * Account-level eSIM promo cards: fetches `/esim/orders` and reads `benefitOrderIds` from Redux.
 */
function useAccountEsimPromoCodes(options: UseAccountEsimPromoCodesOptions = {}): EsimPromoCodesView {
  const { enabled = true } = options
  const dispatch = useAppDispatch()
  const accountRegion = useAppSelector(getLoyaltyAccountRegion)
  const esimState = useAppSelector(state => state.loyalty.esimPromoCodes)

  useEffect(() => {
    if (!enabled || !accountRegion) return
    dispatch(fetchEsimPromoCodesAction())
  }, [dispatch, accountRegion, enabled])

  const visibleCodes = useMemo(
    () => esimState.benefitOrderIds
      .map(id => esimState.codesByOrderId[id])
      .filter((entry): entry is App.LoyaltyEsimPromoCodeEntry => !!entry && entry.phase !== 'not_found'),
    [esimState],
  )

  return useMemo(
    () => {
      if (!enabled) return EMPTY_ESIM_PROMO_CODES_VIEW
      return buildEsimPromoCodesView(visibleCodes, {
        codesByOrderId: esimState.codesByOrderId,
        benefitListLoaded: esimState.benefitListLoaded,
      })
    },
    [enabled, visibleCodes, esimState.codesByOrderId, esimState.benefitListLoaded],
  )
}

export default useAccountEsimPromoCodes
